import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Phone, Mail } from "lucide-react";
import api from "@/lib/api";
import { Section } from "@/components/common";
import { formatPrice, feeBadge } from "@/lib/ads";

const FIELDS = [
  ["company", "Company name *", "text", true],
  ["contact_name", "Contact name *", "text", true],
  ["contact_email", "Work email *", "email", true],
  ["contact_phone", "Phone", "tel", false],
  ["website", "Company website", "url", false],
  ["billing_address", "Billing address", "text", false],
  ["city", "City", "text", false],
  ["state", "State", "text", false],
  ["zip", "ZIP", "text", false],
];

const Step = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-[#84CC16] text-[#0B0F17]" : active ? "bg-[#2563EB] text-white" : "bg-slate-800 text-slate-400"}`}>
      {done ? <Check size={14} /> : n}
    </div>
    <span className={`text-sm ${active || done ? "text-white" : "text-slate-500"} hidden sm:inline`}>{label}</span>
  </div>
);

export default function AdvertiseSignup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ company: "", contact_name: "", contact_email: "", contact_phone: "", website: "", billing_address: "", city: "", state: "", zip: "", country: "USA", notes: "" });
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setCfg(r.data));
    api.get("/ad-packages").then((r) => {
      setPackages(r.data);
      const pid = params.get("package");
      if (pid) { const p = r.data.find((x) => x.id === pid); if (p) { setSelected(p); setStep(1); } }
    });
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.company.trim()) e.company = "Required";
    if (!form.contact_name.trim()) e.contact_name = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email)) e.contact_email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!agreed) { toast.error("Please agree to the terms."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ad-orders", { package_id: selected.id, ...form, agreed_terms: true });
      if (selected.checkout_enabled) {
        const { data: co } = await api.post("/payments/checkout", {
          package_id: selected.id, order_id: data.order_id, origin_url: window.location.origin,
        });
        window.location.href = co.checkout_url;
        return;
      }
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  // SUCCESS STATE
  if (result) {
    return (
      <Section className="py-20 max-w-xl text-center">
        <div className="card p-10" data-testid="signup-success">
          <CheckCircle2 className="text-[#84CC16] mx-auto mb-4" size={48} />
          <h1 className="font-display font-bold text-2xl text-white">Request received</h1>
          <p className="text-slate-400 mt-2">Thanks — your request for <span className="text-white font-semibold">{selected?.name}</span> is in. We emailed a confirmation to <span className="text-white">{form.contact_email}</span> and notified our team.</p>
          {!result.email_configured && (
            <p className="text-amber-300/90 text-xs mt-3 flex items-center justify-center gap-1.5"><AlertTriangle size={13} /> Email delivery isn't fully configured yet, so the confirmation email may not arrive until a sending domain is verified. Your request is saved.</p>
          )}
          <p className="text-slate-500 text-sm mt-4">No payment was collected. We'll reach out to confirm availability and next steps.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <button onClick={() => navigate("/advertise/pricing")} className="bg-[#2563EB] hover:bg-[#3B82F6] text-white font-semibold px-5 py-2.5 rounded-lg">Back to pricing</button>
            <button onClick={() => navigate("/")} className="border border-white/15 text-white font-semibold px-5 py-2.5 rounded-lg">Home</button>
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 text-sm text-slate-400">
            <p className="text-white font-semibold">Questions?</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-2">
              <a href="tel:+19127238577" className="inline-flex items-center gap-1.5 text-[#3B82F6]"><Phone size={14} /> 912-723-8577</a>
              <a href="mailto:thomas@12stonedevelopmentgroup.com" className="inline-flex items-center gap-1.5 text-[#3B82F6]"><Mail size={14} /> thomas@12stonedevelopmentgroup.com</a>
            </div>
          </div>
        </div>
      </Section>
    );
  }

  // PACKAGE SELECTOR (no package chosen)
  if (!selected) {
    return (
      <Section className="py-12 sm:py-16">
        <p className="eyebrow mb-2">Request a placement</p>
        <h1 className="font-display font-black text-3xl sm:text-4xl text-white mb-2">Choose a package to begin</h1>
        <p className="text-slate-400 mb-8">Pick the placement or launch-partner option you're interested in.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((p) => (
            <button key={p.id} data-testid={`choose-${p.id}`} onClick={() => { setSelected(p); setStep(1); }}
              className="card p-5 text-left hover:-translate-y-0.5">
              <h3 className="font-display font-bold text-lg text-white">{p.name}</h3>
              <p className="text-slate-400 text-sm mt-1 line-clamp-2">{p.summary}</p>
              <p className="font-display font-bold text-xl text-white mt-3">{formatPrice(p)}</p>
            </button>
          ))}
        </div>
      </Section>
    );
  }

  return (
    <Section className="py-12 sm:py-16 max-w-2xl">
      <button onClick={() => navigate("/advertise/pricing")} className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-6"><ArrowLeft size={15} /> Back to pricing</button>

      <div className="flex items-center justify-between gap-2 mb-8">
        <Step n={1} label="Package" active={step === 1} done={step > 1} />
        <div className="flex-1 h-px bg-white/10 mx-2" />
        <Step n={2} label="Your details" active={step === 2} done={step > 2} />
        <div className="flex-1 h-px bg-white/10 mx-2" />
        <Step n={3} label="Review" active={step === 3} done={false} />
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="card p-6 sm:p-8" data-testid="step-package">
          <p className="eyebrow mb-2">Selected package</p>
          <h2 className="font-display font-bold text-2xl text-white">{selected.name}</h2>
          <p className="text-slate-400 mt-2">{selected.summary}</p>
          <div className="flex items-end gap-2 mt-4">
            <span className="font-display font-black text-3xl text-white">{formatPrice(selected)}</span>
            <span className="mb-1 text-xs text-slate-400">{feeBadge(selected)}</span>
          </div>
          {selected.is_placeholder && <p className="text-amber-400 text-xs mt-2 flex items-center gap-1.5"><AlertTriangle size={12} /> Placeholder price — final price confirmed before any payment.</p>}
          <div className="mt-4 space-y-1.5">
            {(selected.included || []).map((inc, i) => <div key={i} className="flex items-start gap-2 text-sm text-slate-300"><Check size={15} className="text-[#84CC16] mt-0.5 shrink-0" /> {inc}</div>)}
          </div>
          <button data-testid="to-step-2" onClick={() => setStep(2)} className="mt-6 w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2">Continue <ArrowRight size={16} /></button>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="card p-6 sm:p-8" data-testid="step-details">
          <h2 className="font-display font-bold text-xl text-white mb-4">Company &amp; contact details</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type, req]) => (
              <div key={k} className={k === "company" || k === "billing_address" ? "sm:col-span-2" : ""}>
                <input data-testid={`field-${k}`} type={type} placeholder={label} value={form[k]}
                  onChange={(e) => set(k, e.target.value)}
                  className={`w-full bg-[#0B0F17] border rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 ${errors[k] ? "border-red-500" : "border-white/10"}`} />
                {errors[k] && <p className="text-red-400 text-xs mt-1" data-testid={`error-${k}`}>{errors[k]}</p>}
              </div>
            ))}
            <textarea data-testid="field-notes" placeholder="Anything else we should know? (optional)" rows={3} value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="sm:col-span-2 w-full bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep(1)} className="px-5 border border-white/15 text-white rounded-lg">Back</button>
            <button data-testid="to-step-3" onClick={() => { if (validate()) setStep(3); }} className="flex-1 bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold py-3 rounded-lg inline-flex items-center justify-center gap-2">Review <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div className="card p-6 sm:p-8" data-testid="step-review">
          <h2 className="font-display font-bold text-xl text-white mb-4">Review &amp; submit</h2>
          <div className="bg-[#0B0F17] border border-white/10 rounded-lg p-4 space-y-2 text-sm">
            <Row k="Package" v={selected.name} />
            <Row k="Price" v={`${formatPrice(selected)}${selected.is_placeholder ? " (placeholder)" : ""}`} />
            <Row k="Billing" v={selected.fee_type === "one_time" ? "One-time fee" : `Recurring · ${selected.billing_interval || ""}`} />
            <Row k="Company" v={form.company} />
            <Row k="Contact" v={`${form.contact_name} · ${form.contact_email}`} />
            {form.contact_phone && <Row k="Phone" v={form.contact_phone} />}
          </div>

          {selected.cancellation_terms && (
            <div className="mt-4 text-xs text-slate-400 space-y-1">
              {selected.renewal_terms && <p><span className="text-slate-300 font-semibold">Renewal: </span>{selected.renewal_terms}</p>}
              <p><span className="text-slate-300 font-semibold">Cancellation: </span>{selected.cancellation_terms}</p>
            </div>
          )}

          <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="agree-terms">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 w-4 h-4 accent-[#84CC16]" />
            <span className="text-sm text-slate-300">I understand this submits a <strong className="text-white">request</strong> (no payment is collected), the price shown may be a placeholder, and no placement is active until approved by DYORUSA.</span>
          </label>

          <div className="flex gap-3 mt-6">
            <button onClick={() => setStep(2)} className="px-5 border border-white/15 text-white rounded-lg">Back</button>
            <button data-testid="submit-order" onClick={submit} disabled={submitting || !agreed}
              className="flex-1 bg-[#84CC16] hover:bg-[#9ee325] text-[#0B0F17] font-bold py-3 rounded-lg disabled:opacity-50">
              {submitting ? "Submitting…" : cfg?.payments_enabled && selected.checkout_enabled ? "Continue to checkout" : "Request this placement"}
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4"><span className="text-slate-400">{k}</span><span className="text-white text-right">{v}</span></div>
);
